import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import itemsApi from '../itemsApi';
import transformer from '../common/helpers/transformer';
import { addSuccessNotification, addErrorNotification } from '../common/notification/notificationReducer';
import { loadSalt } from '../authentication/authenticationReducer';
import type { Documentation } from '../common/types/documentation';
import type { AppDispatch, DocumentationState, Store } from '../store';
import { DocumentationPayloadType } from '../common/types/general';

const initialState: DocumentationState = {
  documentation: [],
  status: null,
  errorMessage: null,
};

const loadDocumentation = createAsyncThunk<
  Documentation[],
  DocumentationPayloadType,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('documentation/loadDocumentation', async (payloadType, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  await dispatch(loadSalt());
  const data = await itemsApi.loadItems(payloadType);
  if (data != null) {
    return await Promise.all(
      data.items.map(async (item): Promise<Documentation> => {
        const documentation = await transformer.fromBase64ToObjectWithDecryption<Omit<Documentation, 'id'>>(
          item.payload
        );
        // @ts-ignore
        return {
          id: item.id,
          ...documentation,
        };
      })
    );
  }
  if (state.authentication.isInitialized) {
    const errorMessage = 'Die Dokumentation konnte nicht geladen werden.';
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return [];
});

const addDocumentation = createAsyncThunk<
  string,
  { id: string; payloadType: DocumentationPayloadType; payload: string },
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('documentation/addDocumentation', async ({ id, payloadType, payload }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  if (state.authentication.isInitialized) {
    const item = await itemsApi.addItem(id, payload, payloadType);
    if (item != null) {
      dispatch(addSuccessNotification('Die Dokumentation wurde hinzugefügt.'));
      return item.id;
    }
    const errorMessage = `Die Dokumentation mit der ID ${id} konnte nicht hinzugefügt werden.`;
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return '';
});

const editDocumentation = createAsyncThunk<
  boolean,
  { id: string; payload: string },
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('documentation/editDocumentation', async ({ id, payload }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  if (state.authentication.isInitialized) {
    const isEdited = await itemsApi.editItem(id, payload);
    if (isEdited) {
      dispatch(addSuccessNotification('Die Änderungen wurden gespeichert.'));
      return isEdited;
    }
    const errorMessage = 'Die Änderungen konnten nicht gespeichert werden.';
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return false;
});

const deleteDocumentation = createAsyncThunk<
  boolean,
  string,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('documentation/deleteDocumentation', async (id, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  if (state.authentication.isInitialized) {
    const isDeleted = await itemsApi.deleteItem(id);
    if (isDeleted) {
      dispatch(addSuccessNotification('Die Dokumentation wurde gelöscht.'));
      return isDeleted;
    }
    const errorMessage = `Die Dokumentation mit der ID ${id} konnte nicht gelöscht werden.`;
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return false;
});

const documentationSlice = createSlice({
  name: 'documentation',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadDocumentation.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadDocumentation.fulfilled, (state, action) => {
        state.status = 'idle';
        state.documentation = action.payload;
      })
      .addCase(loadDocumentation.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(addDocumentation.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addDocumentation.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(addDocumentation.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(editDocumentation.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(editDocumentation.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(editDocumentation.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(deleteDocumentation.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteDocumentation.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(deleteDocumentation.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase('resetState', (state) => {
        state = initialState;
      });
  },
});

export type { DocumentationState };

export { loadDocumentation, addDocumentation, editDocumentation, deleteDocumentation };

export default documentationSlice.reducer;
